import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  FEEDBACK_REWARD_PASS_REWARD_COPY,
  FEEDBACK_REWARD_PASS_THRESHOLD,
  buildFeedbackRewardPassLoginHref,
  getFeedbackRewardPass,
  getFeedbackRewardPassSourceFromSearch,
  normalizeFeedbackRewardPassPayload,
  shouldOpenFeedbackRewardPassFromSearch,
  startFeedbackRewardPass,
  submitFeedbackRewardPass,
} from './hitLabRadioFeedbackRewardPassApi';
import HitLabRadioListenerVoiceDropPrompt from './HitLabRadioListenerVoiceDropPrompt';

const initialForm = {
  firstName: '',
  cityRegion: '',
  musicTaste: '',
  listenerRole: '',
  standoutTrackId: '',
  standoutReason: '',
  replayTrackId: '',
  whatConnected: '',
  whatDidNotConnect: '',
  roomExperience: '',
  improvementSuggestion: '',
  attestations: {
    honestFeedback: false,
    positiveFeedbackNotRequired: false,
    notPaidStreaming: false,
    adminApprovalRequired: false,
    oneRewardPerCampaign: false,
  },
};

const REQUIRED_FIELDS = [
  'firstName',
  'cityRegion',
  'musicTaste',
  'listenerRole',
  'standoutReason',
  'whatConnected',
  'whatDidNotConnect',
  'roomExperience',
];

const ATTESTATION_LABELS = [
  ['honestFeedback', 'My feedback is honest.'],
  ['positiveFeedbackNotRequired', 'I understand positive feedback is not required.'],
  ['notPaidStreaming', 'I understand this is not paid streaming.'],
  ['adminApprovalRequired', 'I understand approval is required before any JGK reward is issued.'],
  ['oneRewardPerCampaign', 'I understand this pass is limited to one approved reward per JG Player account per campaign.'],
];

const roleOptions = [
  ['listener', 'Listener'],
  ['music_fan', 'Music fan'],
  ['artist', 'Artist'],
  ['producer', 'Producer'],
  ['dj', 'DJ'],
  ['other', 'Other'],
];

const emptyProgress = {
  threshold: FEEDBACK_REWARD_PASS_THRESHOLD,
  distinctTrackCount: 0,
  eligible: false,
  tracks: [],
};

const cleanCount = (value) => {
  const number = Number(value || 0);
  return Number.isFinite(number) ? Math.max(0, Math.floor(number)) : 0;
};

const getPassStatus = (pass) => String(pass?.status || '').trim();

const isFormComplete = (form) =>
  REQUIRED_FIELDS.every((field) => String(form[field] || '').trim()) &&
  Object.values(form.attestations || {}).every(Boolean);

const buildTrackLabel = (track) => {
  const title = String(track?.title || track?.trackId || 'Record').trim();
  const artist = String(track?.artistName || '').trim();
  return artist ? `${title} - ${artist}` : title;
};

export default function HitLabRadioFeedbackRewardPass({
  player,
  locationSearch = '',
  refreshKey = 0,
}) {
  const history = useHistory();
  const isLoggedIn = Boolean(player?.token);
  const source = useMemo(
    () => getFeedbackRewardPassSourceFromSearch(locationSearch),
    [locationSearch],
  );
  const loginHref = useMemo(
    () => buildFeedbackRewardPassLoginHref(source),
    [source],
  );
  const [expanded, setExpanded] = useState(
    shouldOpenFeedbackRewardPassFromSearch(locationSearch),
  );
  const [pass, setPass] = useState(null);
  const [progress, setProgress] = useState(emptyProgress);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(initialForm);

  const status = getPassStatus(pass);
  const heardCount = cleanCount(progress.distinctTrackCount);
  const threshold = cleanCount(progress.threshold) || FEEDBACK_REWARD_PASS_THRESHOLD;
  const eligible = Boolean(progress.eligible || heardCount >= threshold);
  const progressLabel = eligible
    ? `At least ${threshold}`
    : `${Math.min(heardCount, threshold)} / ${threshold}`;
  const proofTracks = Array.isArray(progress.tracks)
    ? progress.tracks
    : Array.isArray(pass?.listenProofSnapshot?.tracks)
      ? pass.listenProofSnapshot.tracks
      : [];
  const complete = isFormComplete(form);

  const loadPass = useCallback(async () => {
    if (!isLoggedIn) {
      setPass(null);
      setProgress(emptyProgress);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = normalizeFeedbackRewardPassPayload(
        await getFeedbackRewardPass({ player }),
      );
      setPass(payload.pass);
      setProgress(payload.progress || emptyProgress);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          'Feedback pass progress could not be loaded.',
      );
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn, player]);

  useEffect(() => {
    if (shouldOpenFeedbackRewardPassFromSearch(locationSearch)) {
      setExpanded(true);
    }
  }, [locationSearch]);

  useEffect(() => {
    void loadPass();
  }, [loadPass]);

  useEffect(() => {
    if (!isLoggedIn || !refreshKey) return;
    const timer = window.setTimeout(() => {
      void loadPass();
    }, 650);
    return () => window.clearTimeout(timer);
  }, [isLoggedIn, loadPass, refreshKey]);

  useEffect(() => {
    if (!isLoggedIn) return undefined;
    const handleFocus = () => {
      void loadPass();
    };
    const handleVisibility = () => {
      if (!document.hidden) void loadPass();
    };
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [isLoggedIn, loadPass]);

  useEffect(() => {
    if (!isLoggedIn || status !== 'started') return undefined;
    const timer = window.setInterval(() => {
      void loadPass();
    }, 18000);
    return () => window.clearInterval(timer);
  }, [isLoggedIn, loadPass, status]);

  const updateField = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const updateAttestation = (field, value) => {
    setForm((current) => ({
      ...current,
      attestations: {
        ...current.attestations,
        [field]: value,
      },
    }));
  };

  const startPass = async () => {
    if (!isLoggedIn) {
      history.push(loginHref);
      return;
    }
    setBusy(true);
    setError('');
    try {
      const payload = normalizeFeedbackRewardPassPayload(
        await startFeedbackRewardPass({ player, source }),
      );
      setPass(payload.pass);
      setProgress(payload.progress || emptyProgress);
      setExpanded(true);
    } catch (err) {
      setError(
        err?.response?.data?.message || 'Feedback pass could not be started.',
      );
    } finally {
      setBusy(false);
    }
  };

  const submitPass = async (event) => {
    event?.preventDefault?.();
    if (!eligible || !complete || busy) {
      setError('Complete the required fields and attestations before submitting.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const payload = normalizeFeedbackRewardPassPayload(
        await submitFeedbackRewardPass({ player, feedback: form }),
      );
      setPass(payload.pass);
      setProgress(payload.progress || emptyProgress);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          'Feedback pass could not be submitted.',
      );
    } finally {
      setBusy(false);
    }
  };

  const renderLoggedOut = () => (
    <div className="hitlab-radio-feedback-pass-body">
      <h3>Start your feedback pass</h3>
      <p>
        Create or continue your free JG Player account, then listen to at least
        5 records inside JG Radio.
      </p>
      <p>Your listens must be tracked while logged in so the pass can be reviewed.</p>
      <strong>{FEEDBACK_REWARD_PASS_REWARD_COPY}</strong>
      <ul>
        <li>Positive feedback is not required.</li>
        <li>Fake praise is not wanted.</li>
        <li>This is not paid streaming.</li>
        <li>Approval is required before any reward is issued.</li>
      </ul>
      <button type="button" className="hitlab-primary-btn" onClick={startPass}>
        Continue as JG Player
      </button>
    </div>
  );

  const renderProgress = () => (
    <div className="hitlab-radio-feedback-pass-progress">
      <div>
        <span>Records heard while logged in</span>
        <strong>{progressLabel}</strong>
      </div>
      <div
        role="progressbar"
        aria-label="Feedback Reward Pass progress"
        aria-valuemin="0"
        aria-valuemax={threshold}
        aria-valuenow={Math.min(heardCount, threshold)}
      >
        <span
          style={{
            width: `${Math.min(100, (heardCount / threshold) * 100)}%`,
          }}
        />
      </div>
    </div>
  );

  const renderForm = () => (
    <form className="hitlab-radio-feedback-pass-form" onSubmit={submitPass}>
      <div className="hitlab-radio-feedback-pass-form-grid">
        <label>
          <span>First name</span>
          <input
            value={form.firstName}
            onChange={(event) => updateField('firstName', event.target.value)}
            autoComplete="given-name"
          />
        </label>
        <label>
          <span>City / region</span>
          <input
            value={form.cityRegion}
            onChange={(event) => updateField('cityRegion', event.target.value)}
            autoComplete="address-level2"
          />
        </label>
        <label>
          <span>Music taste</span>
          <input
            value={form.musicTaste}
            onChange={(event) => updateField('musicTaste', event.target.value)}
          />
        </label>
        <label>
          <span>Listener role</span>
          <select
            value={form.listenerRole}
            onChange={(event) => updateField('listenerRole', event.target.value)}
          >
            <option value="">Choose role</option>
            {roleOptions.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Standout record optional</span>
          <select
            value={form.standoutTrackId}
            onChange={(event) => updateField('standoutTrackId', event.target.value)}
          >
            <option value="">Choose from heard records</option>
            {proofTracks.map((track) => (
              <option key={track.trackId} value={track.trackId}>
                {buildTrackLabel(track)}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Replay record optional</span>
          <select
            value={form.replayTrackId}
            onChange={(event) => updateField('replayTrackId', event.target.value)}
          >
            <option value="">Choose from heard records</option>
            {proofTracks.map((track) => (
              <option key={track.trackId} value={track.trackId}>
                {buildTrackLabel(track)}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label>
        <span>Standout reason</span>
        <textarea
          value={form.standoutReason}
          onChange={(event) => updateField('standoutReason', event.target.value)}
        />
      </label>
      <label>
        <span>What connected</span>
        <textarea
          value={form.whatConnected}
          onChange={(event) => updateField('whatConnected', event.target.value)}
        />
      </label>
      <label>
        <span>What did not connect</span>
        <textarea
          value={form.whatDidNotConnect}
          onChange={(event) => updateField('whatDidNotConnect', event.target.value)}
        />
      </label>
      <label>
        <span>Room experience</span>
        <textarea
          value={form.roomExperience}
          onChange={(event) => updateField('roomExperience', event.target.value)}
        />
      </label>
      <label>
        <span>Improvement suggestion optional</span>
        <textarea
          value={form.improvementSuggestion}
          onChange={(event) =>
            updateField('improvementSuggestion', event.target.value)
          }
        />
      </label>
      <p className="hitlab-listener-proof-form-notice">
        Approved Listener Pass feedback may appear publicly on JG Radio and JG HitLab
        with your first name or public player handle and records-heard context.
        Private contact, account, and payment details are excluded. Positive
        feedback is not required.
      </p>
      <div className="hitlab-radio-feedback-pass-attestations">
        {ATTESTATION_LABELS.map(([field, label]) => (
          <label key={field}>
            <input
              type="checkbox"
              checked={form.attestations[field]}
              onChange={(event) => updateAttestation(field, event.target.checked)}
            />
            <span>{label}</span>
          </label>
        ))}
      </div>
      <button
        type="submit"
        className="hitlab-primary-btn"
        disabled={busy || !eligible || !complete}
      >
        {busy ? 'Submitting' : 'Submit feedback pass'}
      </button>
    </form>
  );

  const renderStarted = () => (
    <div className="hitlab-radio-feedback-pass-body">
      <h3>Your feedback pass</h3>
      {renderProgress()}
      {eligible ? (
        <>
          <p>Your feedback pass is unlocked.</p>
          <p>
            Tell us what connected, what did not, and which record you would
            actually replay.
          </p>
          <strong>{FEEDBACK_REWARD_PASS_REWARD_COPY}</strong>
          {renderForm()}
        </>
      ) : (
        <>
          <p>
            Listening to at least 5 records unlocks the feedback pass. You can
            keep listening as long as you want.
          </p>
          <strong>{FEEDBACK_REWARD_PASS_REWARD_COPY}</strong>
          <button
            type="button"
            className="hitlab-secondary-btn"
            onClick={() => setExpanded(false)}
          >
            Keep listening
          </button>
        </>
      )}
    </div>
  );

  const renderSubmitted = () => (
    <div className="hitlab-radio-feedback-pass-body">
      <h3>Feedback pass submitted</h3>
      <p>Your pass is now in admin review.</p>
      <p>
        Approved feedback passes receive {FEEDBACK_REWARD_PASS_REWARD_COPY}.
        Cashout requests use the existing wallet flow and may follow current
        wallet rules/fees.
      </p>
      <HitLabRadioListenerVoiceDropPrompt player={player} pass={pass} />
    </div>
  );

  const renderApproved = () => (
    <div className="hitlab-radio-feedback-pass-body">
      <h3>Feedback reward approved</h3>
      <p>{FEEDBACK_REWARD_PASS_REWARD_COPY} was added to your account.</p>
      <button
        type="button"
        className="hitlab-primary-btn"
        onClick={() => history.push('/jgktoken#cashout')}
      >
        Request cashout
      </button>
      <HitLabRadioListenerVoiceDropPrompt player={player} pass={pass} />
    </div>
  );

  const renderRejected = () => (
    <div className="hitlab-radio-feedback-pass-body">
      <h3>Feedback pass reviewed</h3>
      <p>
        Your feedback pass was reviewed, but it was not approved for reward
        issuance this time.
      </p>
    </div>
  );

  const renderLoggedIn = () => {
    if (loading && !pass) {
      return (
        <div className="hitlab-radio-feedback-pass-body" role="status">
          Loading feedback pass...
        </div>
      );
    }
    if (!pass) {
      return (
        <div className="hitlab-radio-feedback-pass-body">
          <h3>Start your feedback pass</h3>
          <p>
            Listen to at least 5 distinct records while logged in. Listening to
            at least 5 records unlocks one honest feedback pass for admin review.
          </p>
          <strong>{FEEDBACK_REWARD_PASS_REWARD_COPY}</strong>
          <button
            type="button"
            className="hitlab-primary-btn"
            onClick={startPass}
            disabled={busy}
          >
            {busy ? 'Starting' : 'Start feedback pass'}
          </button>
        </div>
      );
    }
    if (status === 'approved') return renderApproved();
    if (status === 'rejected') return renderRejected();
    if (status === 'submitted') return renderSubmitted();
    return renderStarted();
  };

  return (
    <section
      className={`hitlab-radio-feedback-pass ${expanded ? 'is-expanded' : 'is-collapsed'}`}
      aria-label="JG Radio Feedback Reward Pass"
    >
      <div className="hitlab-radio-feedback-pass-main">
        <button
          type="button"
          className="hitlab-radio-feedback-pass-toggle"
          aria-expanded={expanded}
          onClick={() => setExpanded((current) => !current)}
        >
          <span>Feedback Reward Pass</span>
          <small>
            {status === 'submitted'
              ? 'In review'
              : status === 'approved'
                ? 'Approved'
                : status === 'rejected'
                  ? 'Reviewed'
                  : eligible
                    ? `At least ${threshold} unlocked`
                    : progressLabel}
          </small>
        </button>
        <p>
          Listen to at least 5 records. Submit honest feedback. Approved passes receive{' '}
          {FEEDBACK_REWARD_PASS_REWARD_COPY}.
        </p>
        {!expanded ? (
          <button
            type="button"
            className="hitlab-primary-btn"
            onClick={() => {
              if (!isLoggedIn) {
                history.push(loginHref);
                return;
              }
              if (!pass) {
                void startPass();
                return;
              }
              setExpanded(true);
            }}
          >
            {status === 'submitted'
              ? 'View review status'
              : eligible
                ? 'Submit feedback pass'
                : 'Start feedback pass'}
          </button>
        ) : null}
      </div>
      {expanded ? (
        <>
          {isLoggedIn ? renderLoggedIn() : renderLoggedOut()}
          {error ? (
            <p className="hitlab-radio-feedback-pass-error" role="alert">
              {error}
            </p>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
